import * as React from "react";

import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import InlineAlert from "@/components/providers/inline-alert-msg";
import { getErrorMessage } from "@/hooks/redux-hooks";
import { Link } from "react-router";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import { Eye, EyeOff } from "lucide-react";

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

interface RegisterFormProps extends React.HTMLAttributes<HTMLDivElement> {
  handleSubmit: (data: SSOCreateAccountPayload) => void;
  isLoading: boolean;
  isError: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
}

const FormSchema = z.object({
  fullName: z.string().min(3),
  username: z.string().email("Email is required to create an account"),
  password: z.string().min(6, {
    message: "Your password must be atleast 6 characters",
  }),
});

export default function RegisterForm({
  className,
  handleSubmit,
  isLoading,
  isError,
  error,
}: RegisterFormProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const res: SSOCreateAccountPayload = {
      email: data.username,
      password: data.password,
      fullName: data.fullName,
    };
    handleSubmit(res);
  };

  return (
    <motion.div
      className={cn("grid gap-6", className)}
      initial="hidden"
      animate="visible"
      variants={formVariants}
    >
      {isError && error && (
        <InlineAlert
          title=""
          mtype={"error"}
          message={getErrorMessage(error)}
        />
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <motion.div className="grid gap-2" variants={itemVariants}>
            <motion.div className="grid gap-1" variants={itemVariants}>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>

            <motion.div className="grid gap-1" variants={itemVariants}>
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fullname</FormLabel>
                    <FormControl>
                      <Input disabled={isLoading} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>

            <motion.div className="grid gap-1" variants={itemVariants}>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          disabled={isLoading}
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>

            <div className="flex gap-1 mb-2 mt-2 justify-between">
              <span className="text-muted-foreground text-sm">
                <Link to="/auth/login">I already have an account</Link>
              </span>
            </div>

            <Button disabled={isLoading}>
              {isLoading && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Account
            </Button>
          </motion.div>
        </form>
      </Form>
    </motion.div>
  );
}
